import React, {
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { TextInput as NativeTextInput } from 'react-native';
import { useSelector } from 'react-redux';
import { BrandColor } from '@styleseat/brand';
import type { RootState } from '../../../../store/models';

import { getOS } from '../../../../modules/AppInfo';

import {
  useLinkTo,
  useDebouncedEffect,
  useJanitor,
  useMount,
  useAndAssignTest,
} from '../../../../hooks';
import fetchSuggestions from '../../../../modules/consumer/search/Autocomplete';
import analytics from '../../../../modules/analytics';
import nonCriticalException from '../../../../modules/exceptionLogger';
import { slugifySearchParams } from '../../../../store/Search.model';
import { SEARCH_PARAM_RESET_CODE } from '../../../../modules/consumer/search/Constants';
import { tiles } from '../../../shared/homepage/SearchTileGrid/SearchGridTiles';
import SearchForm from './SearchForm';
import {
  IQuerySuggestion,
  IQuerySuggestionList,
  SearchSubmitter,
  QueryResultType,
  LocationObject,
} from './types';

import {
  LOCATION_INPUT_CLICKED,
  LOCATION_INPUT_UPDATED,
  LOCATION_ITEM_CLICKED,
  SERVICE_ITEM_CLICKED,
  PRO_ITEM_CLICKED,
  SEARCH_BTN_CLICKED,
  FIND_MY_LOCATION_CLICKED,
  FIND_MY_LOCATION_ERRORED,
  FIND_MY_LOCATION_RESOLVED,
  CLIENT_SEARCH_EXPANSION_TEST_NAME,
  CLIENT_SEARCH_EXPANSION_TEST_PERCENT,
  SEARCH_SUGGESTION_EXPANDED,
} from './constants';
import SearchInput from '../SearchInput';
import { BodyText } from '../../../ui';
import usePopularMatchSuggestions from './usePopularMatchSuggestions';
import {
  IQueryProviderResult,
  IQueryResult,
  IQueryServiceResult,
} from '../../../../modules/consumer/search/types';
import { SEARCH_ROUTE } from '../../../../route-names';
import { getLocationSuggestions, LocationSuggestionResult } from '../../../../modules/maps/locationAutocomplete';
import { LocationRecord, LocationState } from '../../../../store/Location.model';
import store from '../../../../store';

// Re-query geolocation after 1 minute
// This only applies to "Use Current Location" user action
const MAX_GEOLOCATION_AGE = 60 * 1000;

// use the search tiles as the popular search list
const getFormattedPopularServices = (): IQuerySuggestionList[] => (
  [
    {
      title: 'Popular Searches',
      testId: 'search-form-popular-search-section',
      data: tiles.map((tile, i): IQuerySuggestion => ({
        id: tile.text + i,
        text: tile.text,
        testId: `search-form-popular-search-${i}`,
        matchedSubstrings: [
          0,
          tile.text.length - 1,
        ],
        section: QueryResultType.PopularService,
        data: {
          search_query: { query: tile.text },
          display_text: tile.text,
          matchedSubstrings: [],
        },
      })),
    },
  ]
);

const formatQueryList = (queryResults: IQueryResult): IQuerySuggestionList[] => {
  const services = {
    title: 'Services',
    testId: 'search-form-service-suggestion-section',
    data: queryResults.services.map((item, i) => ({
      id: item.display_text,
      text: item.display_text,
      testId: `search-form-service-suggestion-${i}`,
      matchedSubstrings: item.matchedSubstrings,
      section: QueryResultType.Service,
      data: item,
    })),
  };
  const stylists = {
    title: 'Stylists',
    testId: 'search-form-pro-suggestion-section',
    data: queryResults.pros.map((pro, i) => ({
      id: pro.id,
      text: pro.name,
      testId: `search-form-pro-suggestion-${i}`,
      matchedSubstrings: pro.matchedSubstrings,
      section: QueryResultType.Stylist,
      data: pro,
    })),
  };
  const salons = {
    title: 'Salons',
    testId: 'search-form-salon-suggestion-section',
    data: queryResults.salons.map((salon, i) => ({
      id: salon.id,
      text: salon.name,
      testId: `search-form-salon-suggestion-${i}`,
      matchedSubstrings: salon.matchedSubstrings,
      section: QueryResultType.Salon,
      data: salon,
    })),
  };
  const formattedList = [
    services,
    stylists,
    salons,
  ];

  return formattedList;
};

const formatLocationSuggestion = (item: LocationObject): IQuerySuggestion => ({
  id: item.placeId,
  text: item.address,
  matchedSubstrings: [],
  section: QueryResultType.Location,
  data: item,
});

const hasSearchResults = (list: IQuerySuggestionList[]): boolean => {
  let hasItems = false;
  list.forEach(item => {
    hasItems = hasItems || !!item.data.length;
  });
  return hasItems;
};

const isGeolocationExpired = (newLocationState: LocationState) => {
  const { created = 0 } = newLocationState;
  return created < Date.now() - MAX_GEOLOCATION_AGE;
};

const SearchFormContainer = ({
  source,
  initialQuery,
  onSubmit,
  onDirectNavigation,
  showIncentivesStar,
  closeOnSubmit,
}: {
  source: string;
  initialQuery?: string;
  onSubmit?: SearchSubmitter;
  onDirectNavigation?: (name: string, params: Record<string, any>) => void;
  showIncentivesStar: boolean;
  closeOnSubmit?: () => void;
}) => {
  const searchInputRef = useRef<SearchInput>();
  const locationInputRef = useRef<NativeTextInput>();
  const linkTo = useLinkTo();
  const janitor = useJanitor();
  const userLocation = useSelector<RootState, LocationState>(
    store.select.location.userLocation,
  );

  const searchLocation = useSelector<RootState, LocationRecord>(
    state => store.select.search.searchLocation(state),
  );

  const showSubItems = useAndAssignTest(
    CLIENT_SEARCH_EXPANSION_TEST_NAME,
    CLIENT_SEARCH_EXPANSION_TEST_PERCENT,
  );
  const [queryValue, setQueryValue] = React.useState<string | null>(null);
  const [showLocationList, setShowLocationList] = React.useState(false);
  const [locationValue, setLocationValue] = React.useState<string>('');
  const [
    locationObject,
    setLocationObject,
  ] = React.useState<LocationObject | null>(null);
  const [searchSuggestions, setSearchSuggestions] = React.useState<IQuerySuggestionList[]>([]);
  const {
    loadingPopularMatches,
    popularMatchSuggestions,
  } = usePopularMatchSuggestions(initialQuery);
  // only show popularMatchSuggestions once on load
  const [
    hasShownPopularMatchSuggestions,
    setHasShownPopularMatchSuggestions,
  ] = React.useState<boolean>(false);

  const [
    locationSuggestions,
    setLocationSuggestions,
  ] = React.useState<LocationSuggestionResult[]>([]);
  const [isQueryFocused, setIsQueryFocused] = React.useState<boolean>(false);
  const [isLocationFocused, setIsLocationFocused] = React.useState<boolean>(false);
  const [error, setError] = React.useState<React.ReactNode>(null);

  const formattedLocationResults = useMemo(() => (
    [
      {
        title: '',
        data: locationSuggestions.map(formatLocationSuggestion),
      },
    ]
  ), [locationSuggestions]);

  const goTo = useCallback((name: string, params: Record<string, any>) => {
    linkTo(name, params);
    onDirectNavigation?.(name, params);
  }, [
    linkTo,
    onDirectNavigation,
  ]);

  const handleClearQueryValue = () => {
    setQueryValue('');
    searchInputRef.current?.focus();
  };

  const loadSuggestionsForQuery = useCallback((query: string | null) => {
    if (typeof query !== 'string') {
      // set default popular services
      setSearchSuggestions(getFormattedPopularServices());
      return;
    }

    janitor.addPromise(fetchSuggestions(query)).then((results: IQueryResult) => {
      try {
        const formattedResults = formatQueryList(results);
        if (hasSearchResults(formattedResults)) {
          // set search results (if available)
          setSearchSuggestions(formattedResults);
        } else {
          // set default popular services
          setSearchSuggestions(getFormattedPopularServices());
        }
      } catch (e) {
        nonCriticalException(e);
        setSearchSuggestions(getFormattedPopularServices());
      }
    });
  }, [
    janitor,
    setSearchSuggestions,
  ]);

  useDebouncedEffect(() => {
    setShowLocationList(false);

    loadSuggestionsForQuery(queryValue);
  }, [
    queryValue,
    setShowLocationList,
    loadSuggestionsForQuery,
  ], 100);

  // Update search suggestions when the search term changes
  useDebouncedEffect(() => {
    setShowLocationList(false);

    // do nothing while loading if we're in the drill-down test
    if (loadingPopularMatches || showSubItems) {
      return;
    }
    // if popularMatchSuggestions has data, show it (once on load)
    if (!hasShownPopularMatchSuggestions && popularMatchSuggestions?.length) {
      setSearchSuggestions(popularMatchSuggestions);
      setHasShownPopularMatchSuggestions(true);
    } else if (!popularMatchSuggestions?.length) {
      loadSuggestionsForQuery(initialQuery);
    }
  }, [
    initialQuery,
    loadSuggestionsForQuery,
    loadingPopularMatches,
    popularMatchSuggestions,
    showSubItems,
    hasShownPopularMatchSuggestions,
    setHasShownPopularMatchSuggestions,
    setSearchSuggestions,
  ], 100);

  // Update location suggestions when the location term changes
  useDebouncedEffect(async () => {
    const results = await getLocationSuggestions(locationValue, searchLocation, 8);
    try {
      setLocationSuggestions(results);
    } catch (e) {
      nonCriticalException(e);
    }
  }, [locationValue], 250);

  const updateLocation = (location: LocationObject) => {
    // update text field
    setLocationValue(location.address || '');
    // save object for location stuff
    setLocationObject(location);
    setShowLocationList(false);
  };

  // set location on click
  const handleSelectLocation = (suggestion: IQuerySuggestion) => {
    updateLocation(suggestion.data as LocationObject);
    locationInputRef.current.blur();

    analytics.track(LOCATION_ITEM_CLICKED, {
      location: locationObject?.address,
      popover_source: source,
    });
  };

  const handleGeolocationUpdate = useCallback(async (newLocationState: LocationState) => {
    setLocationObject({
      placeId: '',
      ...newLocationState,
    });
    setLocationValue(newLocationState.address);
    setError(null);
  }, []);

  const handleFindMyLocation = async () => {
    analytics.track(FIND_MY_LOCATION_CLICKED, {
      location: userLocation,
      popover_source: source,
    });
    handleGeolocationUpdate(userLocation);

    // If geolocation is old or not loaded, load it now
    if (!userLocation.loading
      && (!userLocation.address || isGeolocationExpired(userLocation))
    ) {
      if (userLocation.error) {
        analytics.track(FIND_MY_LOCATION_ERRORED, {
          popover_source: source,
        });
        setError(
          <BodyText
            appearance="legal"
            color={BrandColor.Red}
          >
            We can&apos;t find you!
            If you&apos;d like to use your current location, enable location sharing
          </BodyText>,
        );
      } else {
        handleGeolocationUpdate(userLocation);
        analytics.track(FIND_MY_LOCATION_RESOLVED, {
          location: userLocation,
          popover_source: source,
        });
      }
    } else if (!userLocation.loading) {
      // If geolocation is loaded and not expired, use that
      handleGeolocationUpdate(userLocation);
      analytics.track(FIND_MY_LOCATION_RESOLVED, {
        userLocation,
        popover_source: source,
        stored_value_reused: true,
      });
    }
  };

  const handleSetLocationValue = async (value: string) => {
    setShowLocationList(true);
    setLocationValue(value);
    setError(null);
  };

  const handleClearLocationValue = async () => {
    setError(null);
    setShowLocationList(true);
    setLocationValue('');
    locationInputRef.current?.focus();
  };

  // if pro result is clicked, link to profile
  const handleProOrSalonResultPress = useCallback((providerId: number) => {
    goTo('profile', {
      providerId,
      // Since `providerId` sometimes means "vanity url" and sometimes means "provider ID", and
      // because changes to `proId` trigger an update in `responsiveProfile`, we supply the known ID
      // here
      proId: providerId,
      featuredImage: null,
    });
  }, [goTo]);

  const performSearch = (query: string, firstSuggestion: LocationObject = null) => {
    // uses first suggestion if locationList is still showing and nothing was selected.
    const locationToSearch = firstSuggestion || locationObject;
    const slugged = slugifySearchParams({
      source,
      q: query,
      loc: locationToSearch?.address,
      lat: SEARCH_PARAM_RESET_CODE,
      lon: SEARCH_PARAM_RESET_CODE,
    });

    if (onSubmit) {
      onSubmit(query, {
        address: locationToSearch?.address,
        latitude: (locationToSearch as LocationRecord)?.latitude || SEARCH_PARAM_RESET_CODE,
        longitude: (locationToSearch as LocationRecord)?.longitude || SEARCH_PARAM_RESET_CODE,
        placeId: locationToSearch?.placeId,
      }, slugged);
    } else {
      goTo(SEARCH_ROUTE, slugged);
    }
    closeOnSubmit?.();
  };

  const handleTakeFirstLocationSuggestion = (): LocationObject | null => {
    if (!showLocationList) {
      return undefined;
    }
    const newLocationSuggestion = locationSuggestions[0]
      ? locationSuggestions[0]
      : locationObject;

    if (locationValue !== locationObject?.address) {
      updateLocation(newLocationSuggestion);
      analytics.track(LOCATION_INPUT_UPDATED, {
        term: locationValue,
        previous_location: locationObject?.address,
        location: newLocationSuggestion?.address,
        popover_source: source,
      });
      return newLocationSuggestion;
    }
    return null;
  };

  // handle search button click
  const handleSearchPress = (query: string) => {
    analytics.track(SEARCH_BTN_CLICKED, {
      query,
      location: locationObject?.address,
      popover_source: source,
    });
    // this will grab first suggestion if locationList is showing
    const firstSuggestion = handleTakeFirstLocationSuggestion();
    setShowLocationList(false);
    performSearch(query, firstSuggestion);
  };

  // find location on mount
  useMount(async () => {
    setLocationObject(searchLocation);
    setLocationValue(searchLocation.address);

    if (!initialQuery || showSubItems) {
      setSearchSuggestions(getFormattedPopularServices());
    }

    // focus search input
    setTimeout(() => {
      searchInputRef.current?.focus();
      setTimeout(() => {
        if (getOS() === 'ios') {
          /**
           * This fixes a weird issue on ios where the keyboard pushes the
           * modal up cutting off the top of the screen.
           */
          window.scrollTo(0, 0);
        }
      }, 200);
    }, 100);
  });

  // track service pressed and link to search
  const trackProItemPress = (id: number, isSalon = false) => {
    analytics.track(PRO_ITEM_CLICKED, {
      id,
      location: locationObject?.address,
      is_salon: isSalon,
      popover_source: source,
    });
  };

  // track service pressed and link to search
  const trackServiceItemPress = (
    query: string,
    isPopular = false,
    isPopularSearchSuggestion = false,
    prevQuery = '',
  ) => {
    analytics.track(SERVICE_ITEM_CLICKED, {
      query,
      location: locationObject?.address,
      popular_service: isPopular,
      popover_source: source,
      popular_search_suggestion: isPopularSearchSuggestion,
      prev_query: prevQuery,
    });
  };

  const handleLocationFocus = () => {
    setIsLocationFocused(true);
    setShowLocationList(true);
    setError(null);
    analytics.track(LOCATION_INPUT_CLICKED, {
      location: locationObject?.address,
      popover_source: source,
    });
  };

  const handleLocationBlur = () => {
    setIsLocationFocused(false);
  };

  const handleQueryFocus = () => {
    setIsQueryFocused(true);
    setShowLocationList(false);
    setError(null);
  };

  const handleQueryBlur = () => {
    setTimeout(() => {
      setIsQueryFocused(false);
    }, 100);
  };

  const handleSearchItemSelect = (listItem: IQuerySuggestion) => {
    if (listItem.section === QueryResultType.Service) {
      const { data } = listItem as { data: IQueryServiceResult };
      trackServiceItemPress(data.search_query?.query, false);
      performSearch(data.search_query?.query);
    } else if (listItem.section === QueryResultType.Stylist) {
      const { data } = listItem as { data: IQueryProviderResult };
      trackProItemPress(data.id, false);
      handleProOrSalonResultPress(data.id);
    } else if (listItem.section === QueryResultType.Salon) {
      const { data } = listItem as { data: IQueryProviderResult };
      trackProItemPress(data.id, true);
      goTo('salon', { salonId: data.id });
    } else if (
      listItem.section === QueryResultType.PopularService
      || listItem.section === QueryResultType.AlsoSearched
    ) {
      const { data } = listItem as { data: IQueryServiceResult };
      trackServiceItemPress(
        data.search_query?.query,
        true,
        data.isPopularSuggestion,
        initialQuery,
      );
      performSearch(data.search_query?.query);
    }
  };

  const handleSearchItemExpand = (item: IQuerySuggestion) => {
    const { data } = item as { data: IQueryServiceResult };

    analytics.track(SEARCH_SUGGESTION_EXPANDED, {
      query: data.search_query?.query,
      location: locationObject?.address,
      popular_service: item.section === QueryResultType.PopularService,
      popover_source: source,
      popular_search_suggestion: data.isPopularSuggestion,
      prev_query: initialQuery,
    });

    janitor.addPromise(fetchSuggestions(item.text)).then((results: IQueryResult) => {
      try {
        // transform the suggestions into the form we need for our components and filter out any
        // that are a repeat of the "parent" term
        const subItems = results.services.map((subItem, i) => ({
          id: subItem.display_text,
          text: subItem.display_text,
          testId: `search-form-service-sub-suggestion-${i}`,
          matchedSubstrings: subItem.matchedSubstrings,
          section: QueryResultType.Service,
          data: subItem,
        })).filter(subItem => subItem.text !== item.text);

        setSearchSuggestions(
          // insert the "child" list into the correct parent suggestion
          searchSuggestions.map((suggestionList: IQuerySuggestionList, idx: number) => {
            if (idx === 0) {
              // we're in the "popular services" list
              return {
                ...suggestionList,
                data: suggestionList.data.map((suggestionListItem: IQuerySuggestion) => {
                  if (suggestionListItem.id === item.id) {
                    return {
                      ...suggestionListItem,
                      subItems,
                    };
                  }

                  return suggestionListItem;
                }),
              };
            }

            return suggestionList;
          }),
        );
      } catch (e) {
        nonCriticalException(e);
        setSearchSuggestions(getFormattedPopularServices());
      }
    });
  };

  return (
    <SearchForm
      queryValue={queryValue === null ? initialQuery : queryValue}
      onChangeQueryValue={setQueryValue}
      showLocationList={showLocationList}
      locationValue={locationValue}
      locationResultsValue={formattedLocationResults}
      locationIsLoading={userLocation.loading}
      searchResultsValue={searchSuggestions}
      onLocationFocus={handleLocationFocus}
      onSearchItemSelect={handleSearchItemSelect}
      onChangeLocationValue={handleSetLocationValue}
      onSelectLocation={handleSelectLocation}
      onSearchPress={handleSearchPress}
      onFindLocation={handleFindMyLocation}
      onClearLocationValue={handleClearLocationValue}
      onClearQueryValue={handleClearQueryValue}
      locationIsFocused={isLocationFocused}
      nothingIsFocused={!isLocationFocused && !isQueryFocused}
      onQueryFocus={handleQueryFocus}
      onQueryBlur={handleQueryBlur}
      onLocationBlur={handleLocationBlur}
      onUnfocusLocationResults={handleTakeFirstLocationSuggestion}
      error={error}
      searchInputRef={searchInputRef}
      locationInputRef={locationInputRef}
      showIncentivesStar={showIncentivesStar}
      showSubItems={showSubItems}
      onSearchItemExpand={handleSearchItemExpand}
    />
  );
};

export default SearchFormContainer;
